<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="1200"
    transition="dialog-bottom-transition"
    v-if="serverDataLoaded"
  >
    <div class="bg-white poppins">
      <div class="modal-header py-4 align-center">
        <h4 class="mb-0 font-weight-bolder">{{ $t("edit_sku") }}</h4>
        <div
          class="btn btn-sm btn-icon btn-active-light-info"
          @click="toggleModal"
        >
          <span class="svg-icon">
            <v-icon size="22">mdi-close</v-icon>
          </span>
        </div>
      </div>
      <div class="modal-body d-flex flex-column scroll-y mx-5 py-7">
        <!--begin::Body-->
        <div>
          <div class="row ma-0">
            <div
              class="h-75px py-0 col-12"
              :class="[isClient ? 'col-md-6' : '']"
            >
              <v-select
                v-model="formData.sku_type_id"
                :label="$t('sku_type')"
                item-text="text"
                item-value="index"
                :items="serverData.sku_types"
                clearable
                outlined
                dense
                :error-messages="sku_type_idErrors"
                @input="$v.formData.sku_type_id.$touch()"
                @blur="$v.formData.sku_type_id.$touch()"
              />
            </div>

            <div
              class="h-75px py-0 col-12 col-sm-6"
              :class="[isClient ? '' : 'col-md-4']"
            >
              <v-text-field
                v-model="formData.system_code"
                :label="$t('system_code')"
                clearable
                outlined
                dense
                disabled
                :error-messages="system_codeErrors"
                @input="$v.formData.system_code.$touch()"
                @blur="$v.formData.system_code.$touch()"
              />
            </div>
            <div
              class="h-75px py-0 col-12 col-sm-6"
              :class="[isClient ? '' : 'col-md-4']"
            >
              <v-text-field
                v-model="formData.alternative_sku_name"
                :label="$t('sku_label')"
                clearable
                outlined
                dense
              />
            </div>
            <div
              class="h-75px py-0 col-12 col-sm-6"
              :class="[isClient ? '' : 'col-md-4']"
            >
              <v-text-field
                v-model="formData.brand"
                :label="$t('brand')"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.width"
                :label="$t('width')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('width', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.length"
                :label="$t('length')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('length', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.height"
                :label="$t('height')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('height', 0)"
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.weight"
                :label="$t('weight')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('weight', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.cube"
                :label="$t('cube')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('cube', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-4">
              <v-text-field
                v-model="formData.hs_code"
                :label="$t('hs_code')"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12">
              <v-select
                multiple
                v-model="formData.categories"
                :label="$t('categories')"
                item-text="text"
                item-value="index"
                :items="serverData.groups"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-select
                v-model="formData.country_id"
                :label="$t('country_of_origin')"
                item-text="text"
                item-value="index"
                :items="serverData.countries"
                clearable
                outlined
                dense
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-select
                v-model="formData.currency_id"
                :label="$t('currency')"
                item-text="text"
                item-value="index"
                :items="serverData.currencies"
                clearable
                outlined
                dense
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-text-field
                v-model="formData.price"
                :label="$t('price')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('price', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-select
                v-model="formData.uom_type_id"
                :label="$t('uom_type')"
                item-text="text"
                item-value="index"
                :items="serverData.uom_types"
                clearable
                outlined
                dense
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-text-field
                v-model="formData.cost_price"
                :label="$t('cost_price')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('cost_price', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-text-field
                v-model="formData.landed_cost"
                :label="$t('landed_cost')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('landed_cost', 0)"
              />
            </div>

            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-select
                v-model="formData.dg_type_id"
                :label="$t('dg_type')"
                item-text="text"
                item-value="index"
                :items="serverData.dg_types"
                clearable
                outlined
                dense
              />
            </div>
            <div class="h-75px py-0 col-12 col-sm-6 col-md-3">
              <v-text-field
                v-model="formData.count_in_box"
                :label="$t('count_in_box')"
                type="number"
                clearable
                outlined
                dense
                min="0"
              />
            </div>

            <div
              class="h-75px py-0 col-12"
              :class="[isClient ? '' : 'col-md-6']"
            >
              <v-text-field
                v-model="formData.out_of_stock_threshold"
                :label="$t('out_of_stock_notification_threshold')"
                type="Number"
                clearable
                outlined
                dense
                @change="() => validateMinValue('out_of_stock_threshold', 0)"
              />
            </div>
            <div class="h-75px py-0 col-12 col-md-6" v-if="!isClient">
              <v-select
                v-model="formData.storage_type"
                :label="$t('storage_type')"
                item-text="text"
                item-value="index"
                :items="serverData.storage_types"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12">
              <v-text-field
                v-model="formData.dangerous_goods_note"
                :label="$t('dangerous_goods_note')"
                clearable
                outlined
                dense
              />
            </div>

            <div class="h-75px py-0 col-12">
              <v-text-field
                v-model="formData.description"
                :label="$t('description')"
                clearable
                outlined
                dense
              />
            </div>

            <div class="py-0 col-12">
              <v-file-input
                v-model="formData.photo"
                show-size
                counter
                multiple
                dense
                outlined
                accept="image/png, image/jpeg, image/bmp"
                prepend-icon="mdi-camera"
                :label="$t('photo')"
                clearable
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-3 h-75px d-flex align-start ma-0 py-0 ps-3"
            >
              <h5 class="pe-4 pt-5 font-weight-regular">
                {{ $t("expiry_control") }}
              </h5>
              <v-switch v-model="formData.expiry_control" hide-details dense />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0"
            >
              <h5 class="pe-4 pt-5 font-weight-regular">
                {{ $t("serial_number_control") }}
              </h5>
              <v-switch
                v-model="formData.serial_number_control"
                hide-details
                dense
                inset
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0"
            >
              <h5 class="pe-4 pt-5 font-weight-regular">
                {{ $t("force_split") }}
              </h5>
              <v-switch
                v-model="formData.is_force_split"
                hide-details
                dense
                inset
                :true-value="1"
                :false-value="0"
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0"
            >
              <h5 class="pe-4 pt-5 font-weight-regular">
                {{ $t("temperature_control") }}
              </h5>
              <v-switch
                v-model="formData.temperature_control"
                hide-details
                dense
                inset
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0"
            >
              <h5 class="pe-4 pt-5 font-weight-regular">
                {{ $t("preorder_enable") }}
              </h5>
              <v-switch
                v-model="formData.is_pre_order_enabled"
                hide-details
                dense
                inset
              />
            </div>

            <div
              class="col-12 col-sm-6 col-md-4 h-75px d-flex align-start ma-0 py-0"
            >
              <h5 class="pr-4 pt-5 font-weight-regular">Shipping required</h5>
              <v-switch
                v-model="formData.is_shipping_required"
                hide-details
                dense
                inset
              />
            </div>
          </div>

          <div class="mt-4 pa-0 w-100">
            <SkuDatatable :setSku="setSkus" :codes="formData.codes" />
          </div>
        </div>

        <!--end::Body-->
        <div class="my-4 h-75px d-flex flex-grow-1 align-end justify-center">
          <button
            type="reset"
            class="btn btn-light me-3 px-5 py-3 ls1"
            @click="resetEditForm"
          >
            {{ $t("reset") }}
          </button>
          <button
            type="submit"
            class="btn btn-info px-5 py-3 ls1"
            @click="submitEditForm"
          >
            {{ $t("submit") }}
          </button>
        </div>
      </div>
    </div>
  </v-dialog>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import ApiService from "@/core/services/api.service";
import SkuDatatable from "@/own/components/fulfillment/skus/skuAddContainer/SkuDatatable";
import SwalEdited from "@/core/services/AlertServices/SwalEdited";
import fieldValueValidation from "@/own/mixins/fieldValueValidation.js";
export default {
  name: "EditItem",
  mixins: [validationMixin, fieldValueValidation],
  components: {
    SkuDatatable,
  },
  validations: {
    formData: {
      system_code: { required },
      sku_type_id: { required },
    },
  },
  props: ["refresher", "pageLoader"],
  data: () => ({
    dialog: false,
    serverData: null,
    formData: {
      id: null,
      alternative_sku_name: null,
      brand: null,
      categories: [],
      country_id: null,
      currency_id: null,
      cost_price: null,
      landed_cost: null,
      cube: null,
      codes: [],
      description: null,
      expiry_control: 0,
      height: null,
      hs_code: null,
      length: null,
      photo: null,
      price: null,
      serial_number_control: 0,
      sku_type_id: null,
      system_code: null,
      weight: null,
      width: null,
      is_force_split: 0,
      uom_type_id: null,
      dg_type_id: null,
      dangerous_goods_note: null,
      temperature_control: 0,
      is_pre_order_enabled: 0,
      out_of_stock_threshold: null,
      count_in_box: null,
      storage_type: 1,
      is_shipping_required: 1,
    },
  }),
  methods: {
    loadDataFromServer() {
      this.pageLoader(true);
      let data = { id: this.$store.getters.getItemForAction.id };
      ApiService.post("/fulfillment/skus/edit", data)
        .then((response) => {
          this.serverData = response.data;
          this.formData = response.data.sku;
          this.formData.photo = null;
          this.pageLoader(false);
        })
        .catch(() => {
          this.dialog = false;
          this.loader(false);
        });
    },

    handleFormValidation(fieldName, vueObj) {
      const errors = [];
      if (!vueObj.$v.formData[fieldName].$dirty) return errors;

      if ("required" in vueObj.$v.formData[fieldName]) {
        !vueObj.$v.formData[fieldName].required &&
          errors.push(this.$t("validation_this_field_is_required"));
      }
      return errors;
    },
    setSkus(item) {
      this.formData.codes = [...item];
    },
    convertToFormData() {
      // console.log(this.formData);
      let data = new FormData();
      Object.entries(this.formData).forEach((item) => {
        if (
          (item[1] && item[0] !== "image" && item[0] !== "photo") ||
          item[1] === 0
        ) {
          if (Array.isArray(item[1])) {
            item[1].forEach((it, index) => {
              if (typeof it === "object" && !Array.isArray(it) && it !== null) {
                Object.entries(it).forEach((el) => {
                  data.append(`${item[0]}[${index}][${el[0]}]`, el[1]);
                });
              } else {
                data.append(`${item[0]}[]`, it);
              }
            });
          } else {
            data.append(item[0], item[1]);
          }
        } else if (item[1] && (item[0] === "image" || item[0] === "photo")) {
          data.append(item[0], item[1][0]);
        }
      });
      return data;
    },
    submitEditForm() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }
      this.pageLoader(true);
      let data = this.convertToFormData();
      ApiService.post("/fulfillment/skus/update", data)
        .then(() => {
          SwalEdited();
          this.toggleModal();
          this.refresher();
          this.pageLoader(false);
        })
        .catch(() => {
          this.pageLoader(false);
        });
    },
    resetEditForm() {
      this.$v.$reset();
      this.formData = {
        system_code: null,
        sku_type_id: null,
        alternative_sku_name: null,
        description: null,
        weight: null,
        cube: null,
        length: null,
        width: null,
        height: null,
        brand: null,
        categories: [],
        codes: [],
        expiry_control: 0,
        serial_number_control: 0,
        hs_code: null,
        country_id: null,
        currency_id: null,
        price: null,
        cost_price: null,
        landed_cost: null,
        is_force_split: 0,
        uom_type_id: null,
        dg_type_id: null,
        dangerous_goods_note: null,
        temperature_control: 0,
        is_pre_order_enabled: 0,
        count_in_box: null,
        out_of_stock_threshold: null,
        storage_type: 1,
        is_shipping_required: 1,
      };
    },
    toggleModal() {
      if (this.dialog) this.resetEditForm();
      else this.loadDataFromServer();
      this.dialog = !this.dialog;
    },
  },
  computed: {
    isClient() {
      return this.$store.getters.currentUser.data.is_client;
      // return true;
    },
    serverDataLoaded: function () {
      return !!this.serverData;
    },
    system_codeErrors: function () {
      return this.handleFormValidation("system_code", this);
    },
    sku_type_idErrors: function () {
      return this.handleFormValidation("sku_type_id", this);
    },
  },
};
</script>
